import {IBulkDryRunResult, IBulkFormData} from "../config/bulk";
import {INewTransaction, ITransaction} from "../config/transactions";
import moment from "moment";
import {ITransactionFormOutputData} from "../components/Transactions/Components/Form/config/models";
import {Currency} from "../config/currencies";
import {ILocation} from "../config/location";
// import {ITransactionFormInputData, ITransactionFormOutputData} from "../components/TransactionForm/TransactionForm";

const CONTENT_TYPES = {
    'text/csv': 'csv',
} as any;

export const getFormatFromFile = (file: File): string => {
    const contentType = file.type;

    let format = CONTENT_TYPES[contentType] || '';

    if (format === '') {
        format = file.name.split('').pop();
    }

    if (format === 'undefined') {
        format = '';
    }

    return format;
}

export const convertToNewTransaction = (d: IBulkDryRunResult): INewTransaction => {
    return {
        currency: d.currency,
        amount: d.amount,
        purchased_at: d.date.format("YYYY-MM-DD"),
        description: d.description,
        payment_method_id: d.paymentMethod ? parseInt(d.paymentMethod) : null,
        category_id: d.categoryId,
        company_id: null,
        is_visible: true,
        budget_id: null,
        location: d.location,
    };
}

export const convertToTransactionFormInputData = (d: IBulkDryRunResult): ITransaction => {
    return {
        original_currency: d.currency,
        original_amount: d.amount,
        purchased_at: d.date,
        description: d.description,
        payment_method_id: d.paymentMethod ? parseInt(d.paymentMethod) : null,
        location: d.location || null,
        transaction_id: "",
        amount: 0,
        category: null,
        company: null,
        is_recurring: false,
        main_category: null,
        main_category_id: null,
        payment_method: null,
        category_id: d.categoryId,
        company_id: null
    };
}

export const convertFromTransactionFormInputData = (d: ITransactionFormOutputData): IBulkDryRunResult => {
    return {
        date: moment(d.purchasedAt),
        currency: d.currency,
        amount: d.amount,
        description: d.description || '',
        paymentMethod: d.paymentMethodId?.toString(),
        location: d.location,
        categoryId: d.categoryId,
        errors: null,
    };
}

/**
 * Column mapping configuration for CSV data
 * Maps CSV column names to IBulkFormData fields
 */
export interface ICsvColumnMapping {
    date?: string;
    currency?: string;
    amount?: string;
    description?: string;
    paymentMethod?: string;
    categoryId?: string;
    location?: string;
}

/**
 * Default column name mappings (case-insensitive)
 */
const DEFAULT_COLUMN_MAPPINGS: ICsvColumnMapping = {
    date: 'date',
    currency: 'currency',
    amount: 'amount',
    description: 'description',
    paymentMethod: 'paymentMethod',
    categoryId: 'categoryId',
    location: 'location',
};

/**
 * Finds the index of a column in the headers array (case-insensitive)
 */
const findColumnIndex = (headers: string[], columnName: string | undefined): number | null => {
    if (!columnName) return null;
    const index = headers.findIndex(h => h.toLowerCase().trim() === columnName.toLowerCase().trim());
    return index >= 0 ? index : null;
};

/**
 * Parses a date string to a moment object
 * Tries multiple date formats
 */
const parseDate = (dateStr: string | null | undefined): moment.Moment | null => {
    if (!dateStr) return null;
    
    const formats = [
        'YYYY-MM-DD',
        'DD-MM-YYYY',
        'MM-DD-YYYY',
        'YYYY/MM/DD',
        'DD/MM/YYYY',
        'MM/DD/YYYY',
        'YYYY.MM.DD',
        'DD.MM.YYYY',
        'MM.DD.YYYY',
    ];
    
    for (const format of formats) {
        const parsed = moment(dateStr, format, true);
        if (parsed.isValid()) {
            return parsed;
        }
    }
    
    // Try moment's default parsing
    const parsed = moment(dateStr);
    return parsed.isValid() ? parsed : null;
};

/**
 * Parses a currency string to Currency enum
 */
const parseCurrency = (currencyStr: string | null | undefined): Currency | null => {
    if (!currencyStr) return null;
    
    const upperCurrency = currencyStr.toUpperCase().trim();
    if (Object.values(Currency).includes(upperCurrency as Currency)) {
        return upperCurrency as Currency;
    }
    
    return null;
};

/**
 * Parses a number from a string as a float
 * Handles various formats:
 * - "100.50" (dot as decimal separator)
 * - "100,50" (comma as decimal separator)
 * - "1,000.50" (comma as thousands, dot as decimal)
 * - "1.000,50" (dot as thousands, comma as decimal)
 */
const parseNumber = (numStr: string | null | undefined): number | null => {
    if (!numStr) return null;
    
    let cleaned = numStr.toString().trim();
    
    // Remove currency symbols and spaces
    cleaned = cleaned.replace(/[€$£\s]/g, '');
    
    // Find the last comma and dot positions
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    // Determine which is the decimal separator
    // If the last separator is followed by 1-3 digits, it's likely the decimal separator
    if (lastComma > lastDot) {
        // Comma is the decimal separator (e.g., "1.000,50" or "100,50")
        // Check if comma is followed by 1-3 digits
        const afterComma = cleaned.substring(lastComma + 1);
        if (/^\d{1,3}$/.test(afterComma)) {
            // Comma is decimal separator, remove dots and replace comma with dot
            cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
            // Comma is thousands separator, remove all commas and dots
            cleaned = cleaned.replace(/[,.]/g, '');
        }
    } else if (lastDot > lastComma) {
        // Dot is the decimal separator (e.g., "1,000.50" or "100.50")
        // Check if dot is followed by 1-3 digits
        const afterDot = cleaned.substring(lastDot + 1);
        if (/^\d{1,3}$/.test(afterDot)) {
            // Dot is decimal separator, remove commas (thousands separators)
            cleaned = cleaned.replace(/,/g, '');
        } else {
            // Dot is thousands separator, remove all commas and dots
            cleaned = cleaned.replace(/[,.]/g, '');
        }
    } else {
        // No separators or only one type, remove all commas and dots
        cleaned = cleaned.replace(/[,.]/g, '');
    }
    
    const parsed = parseFloat(cleaned);
    
    return isNaN(parsed) ? null : parsed;
};

/**
 * Parses a category ID from a string
 */
const parseCategoryId = (categoryIdStr: string | null | undefined): number | null => {
    if (!categoryIdStr) return null;
    
    const parsed = parseInt(categoryIdStr.toString().trim());
    return isNaN(parsed) ? null : parsed;
};

/**
 * Parses a location object from a string (basic implementation)
 * This might need to be enhanced based on your location data format
 */
const parseLocation = (locationStr: string | null | undefined): ILocation | null => {
    if (!locationStr) return null;
    
    // Basic implementation - you may need to enhance this based on your location format
    // For now, return null and let the user set it manually
    return null;
};

/**
 * Maps CSV-like data (multi-dimensional array with headers and rows) to IBulkFormData[]
 * 
 * @param csvData - Multi-dimensional array where first row is headers and rest are data rows
 * @param columnMapping - Optional custom column mapping. If not provided, uses default mappings
 * @returns Array of IBulkFormData objects
 */
export const mapCsvDataToBulkFormData = (
    csvData: string[][],
    columnMapping?: ICsvColumnMapping
): IBulkFormData[] => {
    if (!csvData || csvData.length < 2) {
        return [];
    }

    const headers = csvData[0];
    const dataRows = csvData.slice(1);
    const mapping = columnMapping || DEFAULT_COLUMN_MAPPINGS;

    // Find column indices
    const dateIndex = findColumnIndex(headers, mapping.date);
    const currencyIndex = findColumnIndex(headers, mapping.currency);
    const amountIndex = findColumnIndex(headers, mapping.amount);
    const descriptionIndex = findColumnIndex(headers, mapping.description);
    const paymentMethodIndex = findColumnIndex(headers, mapping.paymentMethod);
    const categoryIdIndex = findColumnIndex(headers, mapping.categoryId);
    const locationIndex = findColumnIndex(headers, mapping.location);

    return dataRows.map((row, rowIndex): IBulkFormData => {
        const errors: string[] = [];
        
        // Parse date
        const dateStr = dateIndex !== null ? row[dateIndex] : null;
        const date = parseDate(dateStr);
        if (!date && dateIndex !== null) {
            errors.push(`Invalid date format: ${dateStr}`);
        }

        // Parse currency
        const currencyStr = currencyIndex !== null ? row[currencyIndex] : null;
        const currency = parseCurrency(currencyStr);
        if (!currency && currencyIndex !== null) {
            errors.push(`Invalid currency: ${currencyStr}`);
        }

        // Parse amount
        const amountStr = amountIndex !== null ? row[amountIndex] : null;
        const amount = parseNumber(amountStr);
        if (amount === null && amountIndex !== null) {
            errors.push(`Invalid amount: ${amountStr}`);
        }

        // Parse description
        const description = descriptionIndex !== null ? (row[descriptionIndex] || '').trim() : '';

        // Parse payment method
        const paymentMethod = paymentMethodIndex !== null ? (row[paymentMethodIndex] || '').trim() : undefined;

        // Parse category ID
        const categoryIdStr = categoryIdIndex !== null ? row[categoryIdIndex] : null;
        const categoryId = parseCategoryId(categoryIdStr);

        // Parse location
        const locationStr = locationIndex !== null ? row[locationIndex] : null;
        const location = parseLocation(locationStr);

        return {
            date: date || moment(),
            currency: currency || Currency.EUR,
            amount: amount || 0,
            description: description,
            errors: errors.length > 0 ? errors : null,
            paymentMethod: paymentMethod,
            categoryId: categoryId,
            location: location,
            isDeleted: false,
        };
    });
};
